import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { LogOut, Users, Settings, FileSpreadsheet, Database } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useSizeChartsAccess } from '@/hooks/useSizeChartsAccess';
import { ROUTES, PLANNING_TABS } from '@/lib/constants/routes';
import { useUiStore } from '@/store/ui.store';
import { useNotificationStore } from '@/store/notification.store';
import { ENV_CONFIG } from '@/config/env';
import LogoCollapsed from '@/assets/logo-collapsed.svg';
import LogoExpanded from '@/assets/logo-expanded.svg';
import CalendarIcon from '@/assets/calendar-icon.svg';
import CollapseIcon from '@/assets/collapse-icon.svg';

interface NavigationBarProps {
  isOpen?: boolean;
  onClose?: () => void;
  onExpandChange?: (expanded: boolean) => void;
}

export const NavigationBar = ({ isOpen = false, onClose, onExpandChange }: NavigationBarProps) => {
  const { signOut } = useAuth();
  const { hasSizeChartsAccess } = useSizeChartsAccess();
  const addNotification = useNotificationStore((state) => state.addNotification);
  const isMobile = useUiStore((state) => state.isMobile);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (isMobile && !isOpen) {
      setIsExpanded(false);
    }
  }, [isMobile, isOpen]);

  const handleLogout = async () => {
    try {
      await signOut();
      // Logout always succeeds from UI perspective (local state is cleared even if API fails)
      // But we can show a warning if there was an issue
    } catch (error) {
      // This shouldn't happen since signOut handles errors internally,
      // but just in case, show a notification
      addNotification({
        type: 'warning',
        message: 'Logged out locally, but there was an issue with the server. You may need to sign in again.',
        duration: 5000,
      });
    } finally {
    if (isMobile && onClose) {
      onClose();
      }
    }
  };

  const toggleExpanded = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    if (onExpandChange && !isMobile) {
      onExpandChange(newState);
    }
  };

  const handleNavClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  const showExpanded = isMobile ? isOpen : isExpanded;
  const navWidth = isMobile || showExpanded ? 'w-[260px]' : 'w-[60px]';

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 z-90 bg-black/50 transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Navigation Bar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-100 flex h-screen flex-col bg-[#E7ECFC] pt-6 transition-all duration-500 ease-in-out',
          navWidth,
          showExpanded
            ? 'items-start pl-[15px] pr-[15px] pb-6'
            : 'items-center pl-[15px] pr-[15px] pb-[15px]',
          isMobile && !isOpen && '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className={cn('mb-8 flex h-[46px] items-center', showExpanded ? 'w-full' : 'w-10')}>
          {showExpanded ? (
            <img src={LogoExpanded} alt="CLIV Logo" className="h-[46px] w-auto transition-opacity duration-300" />
          ) : (
            <img src={LogoCollapsed} alt="CLIV Logo" className="h-[46px] w-auto transition-opacity duration-300" />
          )}
        </div>

      {/* Main Navigation */}
      <nav className={cn('flex flex-1 flex-col gap-2', showExpanded && 'w-full')}>
        <NavLink
          to={`${ROUTES.PLANNING}?tab=${PLANNING_TABS.LIST}`}
          onClick={handleNavClick}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-full transition-all duration-300 ease-in-out touch-target',
              showExpanded ? 'h-12 w-full pl-[10px] pr-4' : 'h-10 w-10 justify-center',
              isActive
                ? 'bg-[#B1ECE2] text-[#1E293B]'
                : 'text-[#64748B] hover:bg-[#B1ECE2] hover:text-[#1E293B]'
            )
          }
          title="Planning"
        >
          <div className="flex h-5 w-5 shrink-0 items-center justify-center">
            <img src={CalendarIcon} alt="Calendar" className="h-5 w-5" />
          </div>
          {showExpanded && (
            <span className="text-sm font-medium transition-opacity duration-300">Planning</span>
          )}
        </NavLink>

        <NavLink
          to={ROUTES.INSPECTOR_MANAGEMENT}
          onClick={handleNavClick}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-full transition-all duration-300 ease-in-out touch-target',
              showExpanded ? 'h-12 w-full pl-[10px] pr-4' : 'h-10 w-10 justify-center',
              isActive
                ? 'bg-[#B1ECE2] text-[#1E293B]'
                : 'text-[#64748B] hover:bg-[#B1ECE2] hover:text-[#1E293B]'
            )
          }
          title="Inspector Management"
        >
          <div className="flex h-5 w-5 shrink-0 items-center justify-center">
            <Users className="h-5 w-5" strokeWidth={1.5} />
          </div>
          {showExpanded && (
            <span className="text-sm font-medium transition-opacity duration-300">Inspectors</span>
          )}
        </NavLink>

        <NavLink
          to={ROUTES.CONFIGURATION}
          onClick={handleNavClick}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-full transition-all duration-300 ease-in-out touch-target',
              showExpanded ? 'h-12 w-full pl-[10px] pr-4' : 'h-10 w-10 justify-center',
              isActive
                ? 'bg-[#B1ECE2] text-[#1E293B]'
                : 'text-[#64748B] hover:bg-[#B1ECE2] hover:text-[#1E293B]'
            )
          }
          title="Configuration"
        >
          <div className="flex h-5 w-5 shrink-0 items-center justify-center">
            <Settings className="h-5 w-5" strokeWidth={1.5} />
          </div>
          {showExpanded && (
            <span className="text-sm font-medium transition-opacity duration-300">Configuration</span>
          )}
        </NavLink>

        {hasSizeChartsAccess && (
          <NavLink
            to={ROUTES.SIZE_CHARTS_IMPORTS}
            onClick={handleNavClick}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-full transition-all duration-300 ease-in-out touch-target',
                showExpanded ? 'h-12 w-full pl-[10px] pr-4' : 'h-10 w-10 justify-center',
                isActive
                  ? 'bg-[#B1ECE2] text-[#1E293B]'
                  : 'text-[#64748B] hover:bg-[#B1ECE2] hover:text-[#1E293B]'
              )
            }
            title="Size Charts & Products"
          >
            <div className="flex h-5 w-5 shrink-0 items-center justify-center">
              <FileSpreadsheet className="h-5 w-5" strokeWidth={1.5} />
            </div>
            {showExpanded && (
              <span className="text-sm font-medium transition-opacity duration-300">Size Charts</span>
            )}
          </NavLink>
        )}

        <NavLink
          to={ROUTES.MASTER_DATA}
          onClick={handleNavClick}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-full transition-all duration-300 ease-in-out touch-target',
              showExpanded ? 'h-12 w-full pl-[10px] pr-4' : 'h-10 w-10 justify-center',
              isActive
                ? 'bg-[#B1ECE2] text-[#1E293B]'
                : 'text-[#64748B] hover:bg-[#B1ECE2] hover:text-[#1E293B]'
            )
          }
          title="Master Data"
        >
          <div className="flex h-5 w-5 shrink-0 items-center justify-center">
            <Database className="h-5 w-5" strokeWidth={1.5} />
          </div>
          {showExpanded && (
            <span className="text-sm font-medium transition-opacity duration-300">Master Data</span>
          )}
        </NavLink>
      </nav>

      {/* Bottom Utility Items */}
      <div className={cn('flex flex-col gap-3', showExpanded && 'w-full')}>
        {/* Expand/Collapse Button - Desktop only */}
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleExpanded}
            className={cn(
              'rounded-lg text-[#64748B] hover:bg-[#B1ECE2] hover:text-[#1E293B] touch-target transition-all duration-300 ease-in-out',
              showExpanded ? 'h-12 w-full justify-start pl-[10px] pr-4 gap-3' : 'h-10 w-10'
            )}
            title={showExpanded ? 'Collapse Menu' : 'Expand Menu'}
          >
            <div className="flex h-5 w-5 shrink-0 items-center justify-center">
              {showExpanded ? (
                <img src={CollapseIcon} alt="Collapse" className="h-5 w-5 transition-opacity duration-300" />
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="transition-opacity duration-300">
                  <rect x="2" y="2" width="16" height="16" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                  <line x1="7" y1="2" x2="7" y2="18" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M11 8L13 10L11 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            {showExpanded && (
              <span className="text-sm font-medium transition-opacity duration-300">Collapse</span>
            )}
          </Button>
        )}

        {/* Logout Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleLogout}
          className={cn(
            'rounded-lg text-[#64748B] hover:bg-[#B1ECE2] hover:text-[#1E293B] touch-target transition-all duration-300 ease-in-out',
            showExpanded ? 'h-12 w-full justify-start gap-3 pl-[10px] pr-4' : 'h-10 w-10'
          )}
          title="Log Out"
        >
          <div className="flex h-5 w-5 shrink-0 items-center justify-center">
            <LogOut className="h-5 w-5" strokeWidth={1.5} />
          </div>
          {showExpanded && (
            <span className="text-sm font-medium transition-opacity duration-300">Log Out</span>
          )}
        </Button>

        {/* App Version */}
        <div className={cn('mt-1 text-[9px] text-[#64748B]', showExpanded ? 'text-left' : 'text-center')}>
          {showExpanded ? (
            <span>Version {ENV_CONFIG.APP_VERSION}</span>
          ) : (
            <span title={`${ENV_CONFIG.APP_VERSION}`}>{ENV_CONFIG.APP_VERSION}</span>
          )}
        </div>
      </div>
    </aside>
    </>
  );
};

